import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Loader2, ImagePlus } from 'lucide-react';

const API = '/api';

const ADV_TYPES = ['Sponsor', 'Interstitial', 'Banner'];

// ── Image picker (shared pattern) ─────────────────────────────────────────────

const ImagePicker = ({ currentUrl, onChange, onRemove, t }) => {
  const fileRef = useRef(null);
  return (
    <div className="space-y-2">
      {currentUrl && (
        <div className="relative inline-block">
          <img src={currentUrl} alt="" className="h-20 rounded-lg border border-gray-200 object-contain bg-gray-50" />
          <button
            type="button"
            onClick={onRemove}
            className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition shadow-sm"
          >
            <X size={12} />
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition">
          <ImagePlus size={14} /> {t.cm_field_image_upload}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = ev => onChange({ base64: ev.target.result.split(',')[1], filename: file.name, mimeType: file.type, preview: ev.target.result });
          reader.readAsDataURL(file);
        }} />
      </div>
    </div>
  );
};

// ── Form modal ────────────────────────────────────────────────────────────────

const AdvForm = ({ item, onSave, onCancel, subEventId, orgHeaders, t }) => {
  const isNew = !item?.id;
  const [form, setForm]   = useState({
    title:            item?.fields?.title            ?? '',
    Status:           item?.fields?.Status           ?? 'on',
    Type:             item?.fields?.Type             ?? 'Sponsor',
    Position:         item?.fields?.Position         ?? 1,
    'publishing date': item?.fields?.['publishing date'] ? item.fields['publishing date'].slice(0, 10) : '',
  });
  const [imgData, setImgData]       = useState(null);
  const [imgRemoved, setImgRemoved] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const currentImgUrl = item?.fields?.Img?.[0]?.thumbnails?.large?.url ?? item?.fields?.Img?.[0]?.url ?? null;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setError('');
    const fields = { ...form };
    if (fields['publishing date']) fields['publishing date'] = new Date(fields['publishing date']).toISOString();
    else delete fields['publishing date'];

    const body = { fields };
    if (imgData) body.image = { base64: imgData.base64, filename: imgData.filename, mimeType: imgData.mimeType };
    if (imgRemoved && !imgData) body.removeImage = true;

    const url    = isNew ? `${API}/admin/advertising/${subEventId}` : `${API}/admin/advertising/${subEventId}/${item.id}`;
    const method = isNew ? 'POST' : 'PATCH';
    try {
      const res  = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...orgHeaders() }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || t.cm_error_save);
      onSave(data.data);
    } catch (e) {
      setError(e.message); setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{isNew ? `${t.cm_btn_add} — ${t.cm_advertising}` : `${t.cm_btn_edit} — ${t.cm_advertising}`}</h3>
          <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">{t.cm_field_title} *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{t.cm_field_type}</label>
              <select value={form.Type} onChange={e => set('Type', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
                {ADV_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{t.cm_field_status}</label>
              <select value={form.Status} onChange={e => set('Status', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
                <option value="on">{t.cm_status_on}</option>
                <option value="off">{t.cm_status_off}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{t.cm_field_position}</label>
              <input type="number" min="1" value={form.Position} onChange={e => set('Position', parseInt(e.target.value) || 1)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{t.cm_field_date}</label>
              <input type="date" value={form['publishing date']} onChange={e => set('publishing date', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2">{t.cm_field_image}</label>
            <ImagePicker
              currentUrl={imgRemoved ? null : (imgData?.preview ?? currentImgUrl)}
              onChange={d => { setImgData(d); setImgRemoved(false); }}
              onRemove={() => { setImgData(null); setImgRemoved(true); }}
              t={t}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">{t.cm_btn_cancel}</button>
          <button onClick={handleSave} disabled={saving || !form.title} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? t.cm_saving : t.cm_btn_save}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

const AdvertisingManager = ({ subEventId, orgHeaders, t }) => {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError]       = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/admin/advertising/${subEventId}`, { headers: orgHeaders() });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      const sorted = (data.data || []).sort((a, b) => (a.fields?.Position ?? 99) - (b.fields?.Position ?? 99));
      setItems(sorted);
    } catch { setError(t.cm_error_load); }
    finally { setLoading(false); }
  }, [subEventId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (item) => {
    if (!window.confirm(`${t.cm_confirm_delete}\n${t.cm_confirm_delete_msg}`)) return;
    setDeleting(item.id);
    try {
      const res  = await fetch(`${API}/admin/advertising/${subEventId}/${item.id}`, { method: 'DELETE', headers: orgHeaders() });
      const data = await res.json();
      if (!data.success) throw new Error();
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch { setError(t.cm_error_delete); }
    finally { setDeleting(null); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-gray-900">{t.cm_advertising}</h2>
        <button onClick={() => setEditing('new')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition">
          <Plus size={14} /> {t.cm_btn_add}
        </button>
      </div>

      {error && <div className="mx-6 mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-blue-500" size={24} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">{t.cm_no_items}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.cm_col_position}</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.cm_col_title}</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.cm_col_type}</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.cm_col_status}</th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.cm_col_actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.map(item => (
              <tr key={item.id} className="hover:bg-gray-50 transition">
                <td className="px-6 py-3 text-gray-500 font-mono text-xs">{item.fields?.Position ?? '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {item.fields?.Img?.[0]?.thumbnails?.small?.url && (
                      <img src={item.fields.Img[0].thumbnails.small.url} alt="" className="w-10 h-10 rounded-lg object-contain bg-gray-100 flex-shrink-0" />
                    )}
                    <span className="font-semibold text-gray-900">{item.fields?.title ?? '—'}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600">{item.fields?.Type ?? '—'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${item.fields?.Status === 'on' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${item.fields?.Status === 'on' ? 'bg-green-500' : 'bg-gray-400'}`} />
                    {item.fields?.Status === 'on' ? t.cm_status_on : t.cm_status_off}
                  </span>
                </td>
                <td className="px-6 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setEditing(item)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition" title={t.cm_btn_edit}><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(item)} disabled={deleting === item.id} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition disabled:opacity-40">
                      {deleting === item.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing !== null && (
        <AdvForm item={editing === 'new' ? null : editing} onSave={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} subEventId={subEventId} orgHeaders={orgHeaders} t={t} />
      )}
    </div>
  );
};

export default AdvertisingManager;
